import { Button, DEFAULT_LANGUAGE, Locale } from '@cibli/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import { LangBridge } from '@/bridge/lang.bridge';
import MainLayout from '@/features/common/components/MainLayout.tsx';
import { useLanguageContext } from '@/features/common/contexts/language.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { getLocales } from '@/services/locale';
import { updateUserLocale } from '@/services/user';

interface LanguageSelectorScreenProps {
  onLanguageSelected: () => void;
  onCancel: () => void;
}

export default function LanguageSelectorScreen({
  onLanguageSelected,
  onCancel,
}: LanguageSelectorScreenProps) {
  const {
    t,
    i18n: { language: i18nLanguage },
  } = useTranslation();
  const { user } = useUserContext();
  const { setLanguage, writeFastpassLanguage } = useLanguageContext();
  const { booth, isBoothDefaultLocaleEnabled } = useBooth();
  const [locales, setLocales] = useState<Locale[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const appliedUserLocaleRef = useRef(false);

  // D2: booth default is only a fallback — an account locale always wins.
  const boothDefaultLocaleId =
    isBoothDefaultLocaleEnabled && booth?.defaultLocaleId ? booth.defaultLocaleId : null;

  // Track selected locale in state so validation uses the clicked value, not i18n
  // (avoids race where user selects French and clicks Continue before i18n has updated,
  // which would persist/send the previous language for SMS and API header)
  const [selectedLocaleCode, setSelectedLocaleCode] = useState(i18nLanguage);
  useEffect(() => {
    setSelectedLocaleCode(i18nLanguage);
  }, [i18nLanguage]);

  // When locales load, normalize selectedLocaleCode to the matched locale's codeLong (e.g. 'fr' -> 'fr-FR')
  useEffect(() => {
    if (!loading && locales.length > 0 && selectedLocaleCode) {
      const matched = locales.find(
        l =>
          l.codeLong === selectedLocaleCode ||
          l.codeShort === selectedLocaleCode ||
          l.codeI18n === selectedLocaleCode,
      );
      if (matched && matched.codeLong !== selectedLocaleCode) {
        setSelectedLocaleCode(matched.codeLong);
      }
    }
  }, [loading, locales, selectedLocaleCode]);

  // When user has an account with a preferred locale, preselect and apply it so the interview uses it
  useEffect(() => {
    if (
      !loading &&
      locales.length > 0 &&
      user?.localeId &&
      locales.some(l => l.codeLong === user.localeId) &&
      !appliedUserLocaleRef.current
    ) {
      appliedUserLocaleRef.current = true;
      setSelectedLocaleCode(user.localeId);
      void setLanguage(user.localeId);
    }
  }, [loading, locales, user?.localeId, setLanguage]);

  // D2 fallback: booth default applies ONLY when neither account locale nor candidate language is set
  useEffect(() => {
    if (
      !loading &&
      locales.length > 0 &&
      boothDefaultLocaleId &&
      !user?.localeId &&
      !appliedUserLocaleRef.current &&
      locales.some(l => l.codeLong === boothDefaultLocaleId)
    ) {
      // If candidate already has an explicit/active language (e.g. chose French on home), don't override with booth default
      const candidateHasExplicitChoice = Boolean(
        selectedLocaleCode &&
        locales.some(
          l =>
            l.codeLong === selectedLocaleCode ||
            l.codeShort === selectedLocaleCode ||
            l.codeI18n === selectedLocaleCode,
        ),
      );

      if (!candidateHasExplicitChoice) {
        appliedUserLocaleRef.current = true;
        setSelectedLocaleCode(boothDefaultLocaleId);
        void setLanguage(boothDefaultLocaleId);
      }
    }
  }, [loading, locales, boothDefaultLocaleId, user?.localeId, selectedLocaleCode, setLanguage]);

  useEffect(() => {
    const fetchLocales = async () => {
      try {
        const fetchedLocales = await getLocales();
        setLocales(fetchedLocales);
      } catch (error) {
        console.error('Error fetching locales:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocales();
  }, []);

  const isLocaleSelected = useCallback(
    (locale: Locale) => {
      const active = selectedLocaleCode || i18nLanguage;
      return (
        locale.codeLong === active || locale.codeShort === active || locale.codeI18n === active
      );
    },
    [selectedLocaleCode, i18nLanguage],
  );

  // Preferred locale first: candidate's selected/active language first, then booth default, then alphabetical
  const sortedLocales = useMemo(() => {
    const active = selectedLocaleCode || i18nLanguage;
    const preferred = boothDefaultLocaleId ?? DEFAULT_LANGUAGE;
    return [...locales].sort((a, b) => {
      const aIsActive = a.codeLong === active || a.codeShort === active || a.codeI18n === active;
      const bIsActive = b.codeLong === active || b.codeShort === active || b.codeI18n === active;
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      if (a.codeLong === preferred && b.codeLong !== preferred) return -1;
      if (b.codeLong === preferred && a.codeLong !== preferred) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [locales, selectedLocaleCode, i18nLanguage, boothDefaultLocaleId]);

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current;
        const canScroll = scrollHeight > clientHeight;
        const isAtBottom = scrollTop >= scrollHeight - clientHeight - 1;
        setShowBottomShadow(canScroll && !isAtBottom);
      }
    };

    checkScrollable();
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current;
        const isAtBottom = scrollTop >= scrollHeight - clientHeight - 1;
        setShowBottomShadow(!isAtBottom);
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Observer pour détecter les changements de taille
      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [locales, loading]);

  const handleLanguageSelect = (locale: Locale) => {
    setSelectedLocaleCode(locale.codeLong);
    void setLanguage(locale.codeLong);
  };

  const validateLanguageSelection = useCallback(async () => {
    // Ensure API header (x-language) and i18n are in sync before proceeding
    await setLanguage(selectedLocaleCode);
    writeFastpassLanguage(selectedLocaleCode);

    // Persist to account when user is in context
    if (user?.id) {
      try {
        await updateUserLocale(selectedLocaleCode, { showErrorToast: false });
      } catch {
        // non-blocking: interview continues with selected language
      }
    }

    // Might cause app restart depending on former and next languages
    await LangBridge.setLanguage(selectedLocaleCode);

    onLanguageSelected();
  }, [setLanguage, writeFastpassLanguage, selectedLocaleCode, user?.id, onLanguageSelected]);

  return (
    <MainLayout align="center" className="gap-12 md:gap-20 py-22 pb-[80px]" onClose={onCancel}>
      <div className="flex flex-col text-center gap-1 sm:gap-2">
        <div className="text-xl text-primary font-semibold">{t('LanguageSelector.title')}</div>
        <h1 className="text-2xl md:text-3xl lg:text-4xl">{t('LanguageSelector.subtitle')}</h1>
      </div>

      {loading && (
        <div className="w-full flex justify-center items-center">
          <div className="text-lg text-foreground">{t('LanguageSelector.loading')}</div>
        </div>
      )}
      {!loading && (
        <div className="relative w-full min-h-0 flex justify-center items-center">
          <div className="flex justify-center relative w-full h-full md:h-auto">
            <div
              ref={scrollContainerRef}
              className="h-full md:h-auto md:max-h-[50vh] overflow-y-auto items-center language-selector-scrollbar pr-4"
            >
              <div className="flex flex-col items-center justify-center min-h-full pl-4">
                <div className="space-y-4 mb-1">
                  {sortedLocales.map(locale => (
                    <div
                      key={locale.codeLong}
                      className={`flex items-center gap-6 p-4 rounded-lg cursor-pointer transition-all duration-200 w-80 ${
                        isLocaleSelected(locale)
                          ? 'bg-white shadow-md border border-gray-200'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleLanguageSelect(locale)}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
                        <ReactCountryFlag
                          countryCode={locale.codeLong.split('-')[1]}
                          svg
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                      <span className="text-xl font-medium text-foreground">{locale.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {showBottomShadow && (
              <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-background to-transparent" />
            )}
          </div>
        </div>
      )}

      <Button
        className="w-full sm:w-auto flex-shrink-0"
        size="lg"
        disabled={loading || locales.length === 0}
        onClick={validateLanguageSelection}
      >
        {t('LanguageSelector.continue')}
      </Button>
    </MainLayout>
  );
}
